/**
 * FYDashboard.jsx — v3
 * ADDED: "Add Bank" button to add a new bank account after FY creation
 * Opening balances remain locked; new bank starts fresh with ₹0 or specified opening.
 */
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, TrendingUp, TrendingDown, Wallet, Building2, Landmark, Plus, AlertCircle } from 'lucide-react';
import { getFYDashboard, addBankToFY } from '../../api/yea.api';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import Modal  from '../../components/common/Modal';
import '../accounting/yea.css';

const StatCard = ({ label, value, icon: Icon, accent, prefix = '₹' }) => (
  <div className="fy-stat-card" style={{ '--accent': accent }}>
    <div className="fy-stat-top">
      <span className="fy-stat-label">{label}</span>
      <div className="fy-stat-icon-wrap">
        {Icon && <Icon size={18} strokeWidth={1.75} />}
      </div>
    </div>
    <div className="fy-stat-value">
      {prefix}{Math.abs(value || 0).toLocaleString('en-IN')}
    </div>
    {value < 0 && <div className="fy-stat-note">Deficit</div>}
  </div>
);

const BankCard = ({ name, balance }) => (
  <div className="fy-bank-card">
    <div className="fy-bank-icon"><Landmark size={14} strokeWidth={1.5} /></div>
    <div>
      <div className="fy-bank-name">{name}</div>
      <div className={`fy-bank-val ${balance < 0 ? 'neg' : ''}`}>
        ₹{Math.abs(balance || 0).toLocaleString('en-IN')}
        {balance < 0 && <span className="fy-bank-dr"> Dr</span>}
      </div>
    </div>
  </div>
);

const FYDashboard = ({ societyId, fyId, fyName }) => {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [showAddBank, setShowAddBank] = useState(false);
  const [bankForm,   setBankForm]   = useState({ bankName: '', accountNumber: '', openingBalance: '' });
  const [saving,     setSaving]     = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getFYDashboard(societyId, fyId);
      setData(r.data.dashboard);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [societyId, fyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddBank = async () => {
    if (!bankForm.bankName.trim()) { toast.error('Bank name is required'); return; }
    try {
      setSaving(true);
      await addBankToFY(societyId, fyId, {
        bankName:      bankForm.bankName.trim(),
        accountNumber: bankForm.accountNumber.trim(),
        openingBalance: parseFloat(bankForm.openingBalance) || 0,
      });
      toast.success(`Bank "${bankForm.bankName}" added successfully`);
      setBankForm({ bankName: '', accountNumber: '', openingBalance: '' });
      setShowAddBank(false);
      fetchData(); // refresh dashboard to show new bank
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to add bank');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!data)   return null;

  return (
    <div className="animate-in">
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem' }}>{fyName || data.fyName}</h2>
          <p className="text-sm text-muted" style={{ marginTop: '0.25rem' }}>Real-time balances</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" icon={Plus} onClick={() => setShowAddBank(true)}>Add Bank</Button>
          <Button variant="ghost" size="sm" icon={RefreshCw} onClick={fetchData}>Refresh</Button>
        </div>
      </div>

      {/* 3 stat cards */}
      <div className="fy-stats-row">
        <StatCard label="Cash Balance"  value={data.cashBalance}  icon={Wallet}       accent="#F5A623" />
        <StatCard label="Total Income"  value={data.totalIncome}  icon={TrendingUp}   accent="#2DD4A0" />
        <StatCard label="Total Expense" value={data.totalExpense} icon={TrendingDown} accent="#F06B6B" />
      </div>

      {/* Bank-wise balances */}
      {data.bankBalances?.length > 0 && (
        <div className="fy-banks-card">
          <div className="fy-banks-header">
            <Building2 size={15} />
            <span>Bank-wise Balances</span>
          </div>
          <div className="fy-banks-grid">
            {data.bankBalances.map((b, i) => (
              <BankCard key={i} name={b.name} balance={b.balance} />
            ))}
          </div>
          <div className="fy-banks-total">
            <span>Total (Cash + All Banks)</span>
            <span className="fy-banks-total-val">
              ₹{(data.totalBalance || 0).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      )}

      {/* Empty bank state */}
      {(!data.bankBalances || data.bankBalances.length === 0) && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', marginTop: '1rem' }}>
          <Landmark size={32} strokeWidth={1} style={{ opacity: 0.25, marginBottom: '0.75rem' }} />
          <p className="text-muted text-sm">No bank accounts added yet.</p>
          <Button size="sm" icon={Plus} onClick={() => setShowAddBank(true)} className="mt-2">Add Bank Account</Button>
        </div>
      )}

      {/* Add Bank Modal */}
      <Modal
        isOpen={showAddBank}
        onClose={() => setShowAddBank(false)}
        title="Add Bank Account"
        subtitle="New bank will be available for future transactions. Existing entries are not affected."
        size="sm"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowAddBank(false)}>Cancel</Button>
            <Button loading={saving} icon={Plus} onClick={handleAddBank}>Add Bank</Button>
          </>
        }
      >
        <div className="txn-form">
          <Input
            label="Bank Name *"
            value={bankForm.bankName}
            onChange={e => setBankForm(f => ({ ...f, bankName: e.target.value }))}
            placeholder="e.g. SBI, HDFC Bank"
          />
          <Input
            label="Account Number"
            value={bankForm.accountNumber}
            onChange={e => setBankForm(f => ({ ...f, accountNumber: e.target.value }))}
            placeholder="Optional"
          />
          <Input
            label="Opening Balance (₹)"
            type="number"
            value={bankForm.openingBalance}
            onChange={e => setBankForm(f => ({ ...f, openingBalance: e.target.value }))}
            placeholder="0"
          />
          <div style={{ padding: '0.65rem 0.9rem', background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem', color: 'var(--info)', display: 'flex', gap: '0.5rem' }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
            Opening balances for existing banks are locked and will not be affected.
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FYDashboard;
